import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useMyMApi from "../../hooks/useMyMApi";
import useWebContext from '../../hooks/useWebContext';
import { MyMLabel, MyMVariantLabel } from '../../ui-elements/MyMLabel';
import { MyMProgress } from '../../ui-elements/MyMProgress';
import { SalesLv1SalesProcessCard } from './SalesLv1SalesProcessCard';
import { SalesLv1SalesProductsCard } from './SalesLv1SalesProductsCard';
import { OutlinedMediaLinkCard } from '../../ui-parts/OutlinedMediaLinkCard';
import PdfViewer from '../../ui-parts/PdfViewer';
import Preview, { previewModalSize } from '../MyM906_Preview/Preview';
import { MyMModal } from '../../ui-elements/MyMModal';
import Typography from "@mui/material/Typography";
import Stack from '@mui/material/Stack';
import Link from '@mui/material/Link';
import useMediaQuery from '@mui/material/useMediaQuery';

/**
 * SalesLv1 Page Component
 */
const SalesLv1 = () => {
  const screenId = 'MyM301';
  const { controlSettings, screenSettings } = useWebContext(screenId);
  const apiRequest = useMyMApi(screenId);
  const [salesContents, setSalesContents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewFileId, setPreviewFileId] = useState(null);

  //2025.7.7 Version3.0対応 Add Start
  const isMobile = useMediaQuery('(max-width:899px)');
  const productsRef = useRef(null);
  const [productsHeight, setProductsHeight] = useState(0);
  useEffect(() => {
    if (productsRef.current) {
      if (isMobile) {
        setProductsHeight(productsRef.current.clientHeight / 2);
      } else {
        setProductsHeight(productsRef.current.clientHeight);
      }
    }
  }, [salesContents]);
  //2025.7.7 Version3.0対応 Add End

  useEffect(() => {
    const callbackRequestSalesContents = (result) => {
      setSalesContents(result);
      setIsLoading(false);
    };
    apiRequest.requestJson('api/sales/lv1', 'GET', callbackRequestSalesContents);
  }, []);
  if (isLoading) return <MyMProgress />;

  const navigateSalesLv2 = (metadata) => {
    navigate(`MyM302/${metadata.Id}`, { state: { metadataName: metadata.Name } });
  };

  //2025.7.7 Version3.0対応 Add Start
  const handleOpenPreview = (fileId) => {
    setPreviewFileId(fileId);
    setIsPreviewOpen(true);
  };
  //2025.7.7 Version3.0対応 Add End

  //2025.7.7 Version3.0対応 Add Start
  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };
  //2025.7.7 Version3.0対応 Add End

  //2025.7.7 Version3.0対応 Add Start
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const options = { day: 'numeric', month: 'long', year: 'numeric' };
    return new Date(dateStr).toLocaleDateString('en-GB', options);
  };
  //2025.7.7 Version3.0対応 Add End

  const cardSolidStyle = '2px solid #D4D4D4';
  const StyledGrid = styled(Grid)(() => ({
    '> .MuiGrid-item': {
      '> .MuiCard-root': { border: cardSolidStyle, },
      '@media (900px <= width)': {
        ':nth-of-type(even)': { '> .MuiCard-root': { borderLeft: 'none', }, },
        ':nth-of-type(n+3)': { '> .MuiCard-root': { borderTop: 'none', }, },
      },
      '@media (width <= 899px)': {
        ':not(:first-of-type)': { '> .MuiCard-root': { borderTop: 'none', }, },
      },
    },
  }));

  const childCardDefaultProps = { borderSolid: cardSolidStyle, navigateSalesLv2, controlSettings, screenId, };

  const pdfUrl = salesContents.PDFFilePath
    ? `${process.env.REACT_APP_BASE_URL}/api/page/file?fileName=${encodeURIComponent(salesContents.PDFFilePath)}`
    : "";

  const anchorSections = [
    { id: "anchor-link1", label: controlSettings.LabelList.lblMiningProducts },
    { id: "anchor-link2", label: controlSettings.LabelList.lblSalesProcessCategory },
    { id: "anchor-link3", label: controlSettings.LabelList.lblPickUpInformationTitle },
    { id: "anchor-link4", label: controlSettings.LabelList.lblSalesDocumentListAndSchedule },
  ];

  const scrollToSection = (id) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: "smooth", block: "start" }); }
  };

  return (
    <Box className='py-1'>
      <Box className='mb-2'>
        <MyMVariantLabel variant='h5'>{controlSettings.LabelList.lblSalseLv1Title}</MyMVariantLabel>
        <MyMLabel>{controlSettings.LabelList.lblSalseMassage}</MyMLabel>
      </Box>

      {isMobile && (
        <Box>
          <MyMLabel sx={{ fontWeight: "bold" }}>{controlSettings.LabelList.lblIndex}</MyMLabel>
          <Box sx={{ position: "sticky", top: 0, zIndex: 1100, bgcolor: "#fff", py: 1, px: 2, boxShadow: 1, marginBottom: '10px' }}>
            <Stack direction="column" spacing={1} alignItems="flex-start">
              {anchorSections.map((sec) => (
                <Link key={sec.id} component="button" variant="body1" underline="hover"
                  onClick={() => scrollToSection(sec.id)}
                  sx={{ cursor: 'pointer', color: 'primary.main', fontWeight: '500', textAlign: 'left', p: 0, minWidth: 80 }}>
                  {sec.label}
                </Link>
              ))}
            </Stack>
          </Box>
        </Box>
      )}

      <Container maxWidth={false} disableGutters sx={{ px: 1 }}>
        <Grid container spacing={2}>

          {/* 左半分 */}
          <Grid item xs={12} md={7}>
            <Box id="anchor-link1" ref={productsRef} sx={{ scrollMarginTop: isMobile ? '80px' : '0' }}>
              <StyledGrid container className='pb-2'>
                <Grid item xs={12} md={6}>
                  <SalesLv1SalesProductsCard
                    {...childCardDefaultProps}
                    lblHeader={controlSettings.LabelList.lblMiningExcavators}
                    imgFileName={controlSettings.ImageList.imgMiningExcavators}
                    lblModels={controlSettings.LabelList.lblMiningExcavatorsModels}
                    lblOldModel={controlSettings.LabelList.lblMiningExcavatorsOldModels}
                    salesProduct={salesContents?.SalesProduct?.MiningExcavatorsProduct}
                    modelGroupColumnCount={4}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SalesLv1SalesProductsCard
                    {...childCardDefaultProps}
                    lblHeader={controlSettings.LabelList.lblRigidDumpTrucks}
                    imgFileName={controlSettings.ImageList.imgRigidDumpTrucks}
                    lblModels={controlSettings.LabelList.lblRigidDumpTrucksModels}
                    lblOldModel={controlSettings.LabelList.lblRigidDumpTrucksOldModel}
                    salesProduct={salesContents?.SalesProduct?.RigidDumpTrucksProduct}
                    modelGroupColumnCount={3}
                  />
                </Grid>
              </StyledGrid>
            </Box>

            <Box id="anchor-link2" sx={{ scrollMarginTop: isMobile ? '80px' : '0', pt: 2 }}>
              <MyMLabel>{controlSettings.LabelList.lblSalesProcessCategory}</MyMLabel>
              <StyledGrid container>
                {(salesContents?.SalesProcessList ?? []).map((process) => (
                  <Grid item xs={12} md={6} className='py-0' key={process.Process1MetadataId}>
                    <SalesLv1SalesProcessCard {...childCardDefaultProps} salesProcess={process} />
                  </Grid>
                ))}
              </StyledGrid>
            </Box>
          </Grid>

          {/* 2025.7.7 Version3.0対応 Add Start */}
          {/* 右半分 */}
          <Grid item xs={12} md={5}>
            <Box
              id="anchor-link3"
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '900px',
                height: isMobile ? 'auto' : productsHeight - 6,
                overflow: 'hidden',
                border: '2px solid #D4D4D4',
                borderRadius: 1,
                backgroundColor: '#fafafa',
                boxSizing: 'border-box',
                scrollMarginTop: isMobile && "80px"
              }}
            >
              {/* PDF表示 */}
              <PdfViewer file={pdfUrl} isMobile={isMobile} />
            </Box>

            <Box sx={{ height: { xs: '20px', md: '45px' } }} />

            <Box
              id="anchor-link4"
              sx={{
                border: '2px solid #D4D4D4',
                borderRadius: 1,
                p: 2,
                display: 'inline-block',
                backgroundColor: '#fff',
                width: '100%',
                scrollMarginTop: isMobile ? "80px" : "0"
              }}
            >
              <MyMLabel style={{ fontSize: '20px', fontWeight: 'bold', paddingBottom: '10px' }}>
                {controlSettings.LabelList.lblSalesDocumentListAndSchedule}
              </MyMLabel>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <OutlinedMediaLinkCard
                    screenId={screenId}
                    mediaHeight={75}
                    mediaImageFileName={controlSettings.ImageList.imgSalesLv1Panel}
                    descriptionHeight={110}
                    caption={controlSettings.LabelList.lblSalesDocumentListHeader}
                    description={controlSettings.LabelList.lblSalesDocumentListDescription}
                    onClick={() => handleOpenPreview(screenSettings.SalesLv1DocListFileID)}
                  >
                    <Typography sx={{ fontSize: '0.8rem', color: '#555', paddingTop: 3 }}>
                      {controlSettings.LabelList.lblPublicationScheduleLastUpdateDate} {formatDate(salesContents.SalesDocumentListLastUpdateDate)}
                    </Typography>
                  </OutlinedMediaLinkCard>
                </Grid>

                <Grid item xs={12} md={6}>
                  <OutlinedMediaLinkCard
                    screenId={screenId}
                    mediaHeight={75}
                    mediaImageFileName={controlSettings.ImageList.imgSalesLv1Panel}
                    descriptionHeight={110}
                    caption={controlSettings.LabelList.lblPublicationScheduleHeader}
                    description={controlSettings.LabelList.lblPublicationScheduleDescription}
                    onClick={() => handleOpenPreview(screenSettings.SalesLv1PubScheduleFileID)}
                  >
                    <Typography sx={{ fontSize: '0.8rem', color: '#555', paddingTop: 3 }}>
                      {controlSettings.LabelList.lblPublicationScheduleLastUpdateDate} {formatDate(salesContents.PublicationScheduleLastUpdateDate)}
                    </Typography>
                  </OutlinedMediaLinkCard>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          {/* 2025.7.7 Version3.0対応 Add End */}

        </Grid>
      </Container>

      {/* プレビュー用モーダル */}
      <MyMModal open={isPreviewOpen} onClose={handleClosePreview} {...previewModalSize}>
        <Preview fileId={previewFileId} onPreviewFailed={handleClosePreview} />
      </MyMModal>
    </Box>
  );
};
export default SalesLv1;